import { useCallback, useEffect, useRef, useState } from 'react';
import { parseCycleCountEvent, type CycleCountEvent } from '../lib/parseCycleCountEvent';

// Cycle Count WebSocket hook. Owns the socket lifecycle via useRef +
// useEffect cleanup, Strict Mode double-invoke safe. Events flow through a
// stable-ref callback (not React state) so a burst of messages in one
// microtask doesn't get batch-dropped by setState (todo 179).

export type SocketStatus = 'idle' | 'connecting' | 'open' | 'closing' | 'closed';

// After a cooperative stop, the server holds the socket open while its LLM
// tail drains, streaming llm_result/progress events the whole time. We wait
// as long as it keeps talking and only force-close after this long with NO
// inbound frame at all — sized above the server's drain idle timeout (45s)
// so the server always closes first on a healthy run.
const STOP_IDLE_TIMEOUT_MS = 60_000;

export interface UseCycleCountSocket {
  status: SocketStatus;
  start: (url: string) => void;
  stop: () => Promise<void>;
  sendBinary: (data: Blob | ArrayBufferLike) => void;
  /** Live snapshot of the underlying WebSocket's outbound buffer size (todo 182). */
  getBufferedAmount: () => number;
  /** Register the callback that receives every parsed event. Overwrites any prior registration. */
  onEvent: (fn: (event: CycleCountEvent) => void) => void;
}

export type WebSocketFactory = (url: string) => WebSocket;

export function useCycleCountSocket(
  factory: WebSocketFactory = (u) => new WebSocket(u),
): UseCycleCountSocket {
  const socketRef = useRef<WebSocket | null>(null);
  const closePromiseRef = useRef<{ promise: Promise<void>; resolve: () => void } | null>(null);
  const onEventRef = useRef<((event: CycleCountEvent) => void) | null>(null);
  const unmountedRef = useRef(false);
  // Timestamp of the last inbound frame. stop()'s idle watchdog compares
  // against this so a still-draining server keeps the socket alive.
  const lastActivityRef = useRef(0);
  const [status, setStatus] = useState<SocketStatus>('idle');

  // Two teardown paths:
  // - requestStop: cooperative — send a {type:"stop"} text frame and let
  //   the server push final metrics and close. Preferred for user-driven
  //   Stop so the metrics event actually reaches the client (Safari drops
  //   incoming messages once the CLIENT has called close()).
  // - forceClose: hard close from our side — for unmount/error paths where
  //   we don't care about a graceful metrics roundtrip.
  const requestStop = useCallback(() => {
    const socket = socketRef.current;
    if (!socket) return;
    if (socket.readyState !== WebSocket.OPEN) return;
    try {
      socket.send(JSON.stringify({ type: 'stop' }));
    } catch {
      // Fall through to force-close if the send fails.
      forceClose('stop-send-failed');
    }
  }, []);

  const forceClose = useCallback((reason: string) => {
    const socket = socketRef.current;
    if (!socket) return;
    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
      try {
        socket.close(1000, reason);
      } catch {
        // Some browsers throw on close before the socket is fully established.
      }
    }
  }, []);

  useEffect(() => {
    // Reset on every mount — under React Strict Mode dev-double-invoke the
    // first cleanup sets unmountedRef true, and without this reset the
    // second mount never flips it back.
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      forceClose('unmount');
    };
  }, [forceClose]);

  const start = useCallback(
    (url: string) => {
      // Guard on ref presence, not just OPEN/CONNECTING (todo 183). If a
      // socket is CLOSING and we create a new one, the closing socket's
      // onclose will resolve the wrong promise.
      if (socketRef.current) {
        return;
      }

      const socket = factory(url);
      socketRef.current = socket;
      setStatus('connecting');

      let resolveClose: (() => void) | null = null;
      const closePromise = new Promise<void>((resolve) => {
        resolveClose = resolve;
      });
      closePromiseRef.current = { promise: closePromise, resolve: resolveClose! };

      socket.onopen = () => {
        if (unmountedRef.current) return;
        setStatus('open');
      };

      socket.onmessage = (evt) => {
        // Any inbound frame counts as liveness for stop()'s idle watchdog.
        lastActivityRef.current = Date.now();
        if (typeof evt.data !== 'string') return;
        const parsed = parseCycleCountEvent(evt.data);
        if (parsed.ok) {
          // Direct callback dispatch — every event flows, no React batching
          // to fight (todo 179). setState-based dispatch dropped bursts.
          onEventRef.current?.(parsed.event);
        }
      };

      socket.onerror = () => {
        // Browsers emit onerror before onclose on connect failure; onclose
        // still fires and drives the state transition.
      };

      socket.onclose = () => {
        // Bind to the specific socket we created here so a stale onclose
        // from a torn-down previous socket can't clear the current ref.
        if (socketRef.current === socket) {
          socketRef.current = null;
        }
        closePromiseRef.current?.resolve();
        closePromiseRef.current = null;
        if (unmountedRef.current) return;
        setStatus('closed');
      };
    },
    [factory],
  );

  const stop = useCallback(async () => {
    const closeRef = closePromiseRef.current;
    if (!unmountedRef.current) setStatus('closing');
    // Cooperative stop: ask the server to end the run. Server streams the
    // remaining llm_result/progress events, pushes final metrics, THEN sends
    // its own Close frame. Our onclose fires afterward and resolves the
    // returned promise.
    lastActivityRef.current = Date.now();
    requestStop();
    if (!closeRef) return;

    // Idle watchdog instead of a fixed cap: a big scan's LLM tail can take
    // minutes to drain, and a fixed timeout used to force-close mid-drain.
    // Poll for the socket going quiet — every inbound frame refreshes
    // lastActivityRef (see onmessage). Only force-close after a full
    // STOP_IDLE_TIMEOUT_MS window with no traffic.
    await new Promise<void>((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        window.clearInterval(timer);
        resolve();
      };
      void closeRef.promise.then(finish);
      const timer = window.setInterval(() => {
        const socket = socketRef.current;
        if (!socket || socket.readyState === WebSocket.CLOSED) {
          finish();
          return;
        }
        if (Date.now() - lastActivityRef.current >= STOP_IDLE_TIMEOUT_MS) {
          // Server went silent — stop waiting. forceClose's onclose resolves
          // closeRef.promise, which drives finish().
          forceClose('stop-idle-timeout');
        }
      }, 1_000);
    });
  }, [forceClose, requestStop]);

  const sendBinary = useCallback((data: Blob | ArrayBufferLike) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    socket.send(data as Blob);
  }, []);

  const getBufferedAmount = useCallback(
    () => socketRef.current?.bufferedAmount ?? 0,
    [],
  );

  const onEvent = useCallback((fn: (event: CycleCountEvent) => void) => {
    onEventRef.current = fn;
  }, []);

  return { status, start, stop, sendBinary, getBufferedAmount, onEvent };
}
